import { motion, useScroll, useTransform, useMotionValue, animate } from "framer-motion";
import React, { useRef, useEffect, useState } from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";
import PhoneMockup from "./PhoneMockup";
import HeroLiveQuote from "./HeroLiveQuote";

const easeCustom = [0.76, 0, 0.24, 1];

export default function Hero() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const LINES = [
    { text: t("hero.line1"), italic: false },
    { text: t("hero.line2"), italic: true },
    { text: t("hero.line3"), italic: false },
  ];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-4, 2]);

  return (
    <section
      ref={ref}
      className="relative pt-32 md:pt-40 lg:pt-44 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero"
    >
      {/* Editorial background layers — subtle gradient + tighter emerald glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 600px at 50% -10%, rgba(74,29,150,0.05), transparent 60%), linear-gradient(180deg, #FAFAFA 0%, #FAFAFA 60%, #EFEBF5 100%)",
        }}
      />
      <motion.div style={{ y: bgY }} aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-16 -left-40 w-[520px] h-[520px] rounded-full bg-lime/10 blur-[110px]" />
        <div className="absolute top-20 right-[-140px] w-[520px] h-[520px] rounded-full bg-forest/8 blur-[120px]" />
      </motion.div>

      <div className="container-x relative">
        {/* eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
          data-testid="hero-eyebrow"
        >
          <span className="live-dot" />
          <span className="label-eyebrow">{t("hero.eyebrow")}</span>
          <span className="hidden sm:inline text-xs text-ink/50">
            {t("hero.tag")}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* LEFT: kinetic reveal + CTA */}
          <div className="lg:col-span-7">
            <h1
              className="font-display font-medium text-forest text-[10vw] sm:text-[8vw] lg:text-[7vw] leading-[0.92] tracking-tightest max-[380px]:text-[12vw] break-words hyphens-auto"
              data-testid="hero-headline"
            >
              {LINES.map((line, i) => (
                <MaskLine key={i} delay={0.1 + i * 0.12}>
                  <span className={line.italic ? "italic font-serif text-forest/85" : ""}>
                    {line.text}
                  </span>
                </MaskLine>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 md:mt-8 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed"
              data-testid="hero-subhead"
            >
              {t("hero.subhead")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.72, duration: 0.7 }}
              className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
              data-testid="hero-ctas"
            >
              <Link to={prefix("/waitlist")} className="btn-accent justify-center sm:justify-start" data-testid="hero-cta-waitlist">
                {t("hero.ctaWaitlist")} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to={prefix("/how-it-works")} className="btn-ghost justify-center sm:justify-start" data-testid="hero-cta-how">
                {t("hero.ctaHow")}
              </Link>
            </motion.div>

            <HeroLiveQuote />

            {/* Trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-12 md:mt-14 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 max-w-lg"
              data-testid="hero-trust"
            >
              <TrustStat kpi="< 3 min" numFrom={9} numTo={3} suffix=" min" prefix="< " label={t("hero.trust.settleLabel")} />
              <TrustStat kpi="0.9%" numFrom={3.4} numTo={0.9} suffix="%" decimals={1} label={t("hero.trust.feeLabel")} />
              <TrustStat kpi="34" numFrom={5} numTo={34} label={t("hero.trust.countriesLabel")} className="col-span-2 sm:col-span-1" />
            </motion.div>
            <p className="text-[11px] text-ink/45 mt-2">{t("hero.trust.feeNote")}</p>
          </div>

          {/* RIGHT: floating phone + orbit */}
          <div className="lg:col-span-5 relative">
            <motion.div
              style={{ y: phoneY, rotate }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.9, ease: easeCustom }}
              className="relative flex justify-center lg:justify-end"
            >
              <PhoneMockup />
            </motion.div>

            {/* Orbiting labels */}
            <FloatingChip
              className="left-0 top-8"
              delay={0.9}
              testId="orbit-mpesa"
              title="M-PESA"
              body="+12,400 KES"
            />
            <FloatingChip
              className="right-2 top-56"
              delay={1.05}
              testId="orbit-qr"
              title="SCAN TO PAY"
              body="EMVCo QR"
              lime
            />
            <FloatingChip
              className="left-4 bottom-16"
              delay={1.2}
              testId="orbit-bank"
              title="BANK · GTB"
              body="₦ 145,220"
            />
          </div>
        </div>

        {/* Under-hero micro row */}
        <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3 text-xs text-ink/60">
            <Sparkles className="w-4 h-4 text-forest" strokeWidth={1.5} />
            {t("hero.trust.backedBy")}
          </div>
          <div className="flex items-center gap-8 opacity-70">
            {["FSCA", "FCA", "DNB", "CBK", "CBN"].map((r) => (
              <span key={r} className="font-mono text-[11px] tracking-[0.22em] text-ink/60">
                {r}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, ease: easeCustom, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function TrustStat({ kpi, label, className = "", numFrom, numTo, prefix = "", suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const mv = useMotionValue(numFrom ?? 0);
  const [display, setDisplay] = useState(kpi);
  const [inView, setInView] = useState(false);

  // Manual IntersectionObserver — reliably re-fires on repeat entries,
  // unlike framer's useInView which occasionally misses re-entries.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3, rootMargin: "0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (numFrom === undefined || numTo === undefined) {
      setDisplay(kpi);
      return;
    }
    if (!inView) {
      mv.set(numFrom);
      setDisplay(`${prefix}${numFrom.toFixed(decimals)}${suffix}`);
      return;
    }
    // Reset then animate for every fresh entry.
    mv.set(numFrom);
    const controls = animate(mv, numTo, {
      duration: 1.1,
      ease: [0.76, 0, 0.24, 1],
      onUpdate: (v) => setDisplay(`${prefix}${v.toFixed(decimals)}${suffix}`),
    });
    return () => controls.stop();
  }, [inView, numFrom, numTo, kpi, prefix, suffix, decimals, mv]);

  return (
    <div className={className} ref={ref} data-testid="hero-trust-stat">
      <div className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tightest text-forest tabular-nums">
        {display}
      </div>
      <div className="mt-1 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-ink/50 leading-snug">{label}</div>
    </div>
  );
}

function FloatingChip({ className = "", title, body, delay = 1, lime = false, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.6, ease: easeCustom }}
      className={`absolute hidden md:block ${className}`}
      data-testid={testId}
    >
      <div
        className={`rounded-2xl px-4 py-3 shadow-[0_20px_50px_-24px_rgba(4,26,16,0.35)] backdrop-blur-md ${
          lime ? "bg-lime/95 text-ink" : "bg-white/90 text-ink border border-forest/10"
        }`}
      >
        <div className="text-[10px] tracking-[0.22em] uppercase opacity-70">{title}</div>
        <div className="mt-1 font-display text-sm">{body}</div>
      </div>
    </motion.div>
  );
}
